// src/components/YearEndClose.js
// Close an academic/fiscal year: advances the posting lock to the day after
// year-end so nothing can post back into the closed year, and records the close.
// No GL closing entries — the Balance Sheet computes retained earnings live.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, MenuItem, Alert, CircularProgress, IconButton, Button, Divider
} from '@mui/material';
import { Home, Lock } from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const YearEndClose = () => {
  const navigate = useNavigate();
  const [years, setYears]       = useState([]);
  const [academicYear, setYear] = useState('');
  const [asOfDate, setAsOfDate] = useState('');
  const [note, setNote]         = useState('');
  const [history, setHistory]   = useState([]);
  const [window_, setWindow]    = useState({ from: '', to: '' });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const loadHistory = useCallback(() => {
    axios.get(`${API}/api/finance/year-end-close`, { headers: hdrs() })
      .then(r => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/api/finance/posting-window`, { headers: hdrs() })
      .then(r => setWindow(r.data || { from: '', to: '' })).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/academics/years`, { headers: hdrs() })
      .then(r => setYears(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const closeYear = async () => {
    if (!academicYear) { setError('Select an academic year'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const { data } = await axios.post(`${API}/api/finance/year-end-close`,
        { academicYear, asOfDate: asOfDate || undefined, note }, { headers: hdrs() });
      setSuccess(`${data.message}. Postings are now locked before ${data.allowPostingFrom}.`);
      setNote('');
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close year');
    } finally { setSaving(false); }
  };

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Lock sx={{ mr: 1, color: '#c62828' }} />
              <Typography variant="h5" color="#c62828" fontWeight="bold">Year-End Close</Typography>
            </Box>
            <IconButton onClick={() => navigate('/finance')} title="Finance Home"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Closing a year moves the posting lock to the day after year-end, so no document can be posted
            back into the closed year. Retained earnings continue to show live on the Balance Sheet — no
            closing journal is posted. An admin can still adjust the lock on the Posting Period screen.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            Current posting lock: {window_.from ? <>allowed from <strong>{window_.from}</strong></> : 'none'}
            {window_.to ? <> to <strong>{window_.to}</strong></> : ''}.
          </Alert>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={1}>
            <TextField select label="Academic Year" value={academicYear} size="small" sx={{ minWidth: 150 }}
              onChange={e => setYear(e.target.value)}>
              <MenuItem value=""><em>— Select —</em></MenuItem>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
            </TextField>
            <TextField type="date" label="Year-end date (optional)" value={asOfDate} size="small"
              onChange={e => setAsOfDate(e.target.value)} InputLabelProps={{ shrink: true }}
              helperText="Defaults to the year's last term end date" />
          </Box>
          <TextField label="Note (optional)" value={note} onChange={e => setNote(e.target.value)}
            fullWidth size="small" sx={{ mb: 2 }} />

          <Button variant="contained" color="error" onClick={closeYear} disabled={saving || !academicYear}
            startIcon={saving ? <CircularProgress size={18} /> : <Lock />}>
            {saving ? 'Closing…' : 'Close Year'}
          </Button>

          {history.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>Closed Years</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Year</strong></TableCell>
                    <TableCell><strong>Year-End</strong></TableCell>
                    <TableCell><strong>Locked Before</strong></TableCell>
                    <TableCell><strong>Note</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map(h => (
                    <TableRow key={h.academicYear} hover>
                      <TableCell>{h.academicYear}</TableCell>
                      <TableCell>{fmtDate(h.lockDate)}</TableCell>
                      <TableCell>{h.allowPostingFrom || '—'}</TableCell>
                      <TableCell>{h.note || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default YearEndClose;
